import React, { useState, useImperativeHandle, useCallback } from 'react';
import { Button, Form, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { auth } from '../../auth/firebaseConfig';
import { GoogleAuthProvider, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { FcGoogle } from "react-icons/fc";
import { getAttributorData } from '../../utils/activityTracking';
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../../redux/user/userSlice";
import UnifiedModal from '../UnifiedModal';
import { useHistory } from 'react-router-dom';
import { trackEvent } from '../../utils/analytics';

// ...existing code...

const LoginModal = React.forwardRef(({ shouldShow, setModalVisible, afterLogin, cid }, ref) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const history = useHistory();

  // Set Firebase Auth language for localized error messages
  auth.languageCode = i18n.language;

  const getAttributor = () => getAttributorData();

  const handleClose = useCallback(() => setModalVisible(false), [setModalVisible]);

  const prefs = useSelector(state => state.prefs);

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    setLoading(true);
    try {
      const lng = i18n.language
      provider.setCustomParameters({ hl: lng });
      const result = await signInWithPopup(auth, provider);
      const idToken = await result.user.getIdToken();
      const res = await fetch(`${process.env.REACT_APP_USERS_BACKEND_URL}/api/auth/firebase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: idToken,
          attributor: getAttributor(),
          externalClientId: cid,
          prefs,
        }),
      });
      const data = await res.json();
      if (data) {
        afterLogin(data, 'FirebaseGoogle')
      }
      trackEvent('Login', {
        event_category: 'User',
        event_label: 'FirebaseLogin'
      });
      handleClose();
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  // Expose handler for tests
  useImperativeHandle(ref, () => ({
    handleGoogleLogin,
  }));

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      if (isRegistering) {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(result.user);
        setEmailSent(true);
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);
        if (!result.user.emailVerified) {
          setError("Please verify your email before logging in.");
          return;
        }
        const idToken = await result.user.getIdToken();
        const res = await fetch(`${process.env.REACT_APP_USERS_BACKEND_URL}/api/auth/firebase`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: idToken,
            attributor: getAttributor(),
            externalClientId: cid,
            prefs,
          }),
        });
        const data = await res.json();
        if (data) {
          afterLogin(data, 'FirebaseEmail')
        }
        trackEvent('Login', {
          event_category: 'User',
          event_label: 'FirebaseLogin'
        });
        handleClose();
      }
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error)
    }
    dispatch(logoutUser());
    afterLogin(null)
  };

  return (
    <UnifiedModal
      show={shouldShow}
      onHide={handleClose}
      title={isRegistering ? t("registerTitle") : t("loginTitle")}
      dir={i18n.dir()}
    >
      <Container>
        {user ? (
          <div className="text-center">
            {user.picture ? (
              <img src={user.picture} alt="Profile" className="rounded-circle mb-3" width="100" />
            ) : (
              <div className="rounded-circle text-white d-flex align-items-center justify-content-center" style={{ backgroundColor: "#87d6e4", width: "48px", height: "48px", fontSize: "30px", marginLeft: "auto", marginRight: "auto" }}>
                {user.name ? user.name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
              </div>
            )}
            <h5>{user.name || user.email}</h5>
            <p>{user.email}</p>
            <Button variant="primary" className="w-100 mt-3" onClick={() => { history.push('/subscriptions'); handleClose(); }}>{t('unsubscribe.manage_subscriptions')}</Button>
            <Button variant="danger" className="w-100 mt-3" onClick={handleLogout}>{t("logout")}</Button>
          </div>
        ) : (
          <>
            <Button variant="light" className="w-100 mb-3 border" onClick={handleGoogleLogin} disabled={loading}>
              <FcGoogle size={22} className={i18n.dir() === 'rtl' ? 'ml-2' : 'mr-2'} /> {t("loginWithGoogle")}
            </Button>
            <div className="text-center text-muted my-3">{t("or")}</div>
            {error && <p className="text-danger text-center">{error}</p>}
            {emailSent && <p className="text-success text-center">{t("verificationEmailSent")}</p>}
            <Form onSubmit={handleEmailAuth}>
              <Form.Group controlId="email">
                <Form.Control
                  type="email"
                  placeholder={t("emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>
              <Form.Group controlId="password" className="mt-3">
                <Form.Control
                  type="password"
                  placeholder={t("passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Form.Group>
              <Button variant="primary" className="w-100 mt-3" type="submit" disabled={loading}>
                {loading ? t("loading") : isRegistering ? t("register") : t("loginWithEmail")}
              </Button>
            </Form>
            <div className="text-center mt-3">
              <small>
                {isRegistering ? t("alreadyHaveAccount") : t("noAccount")}
                <span className="text-primary" style={{ cursor: "pointer" }} onClick={() => setIsRegistering(!isRegistering)}>
                  {isRegistering ? t("login") : t("register")}
                </span>
              </small>
            </div>
          </>
        )}
      </Container>
    </UnifiedModal>
  );
});

export default LoginModal;